/**
 * Domain hook for smart-view count queries.
 *
 * Encapsulates: queryKey + queryFn + createPowerSyncSingle
 * so there is exactly one authoritative PowerSync subscription per session.
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB } from "@listto/powersync";

import { getSmartViewCountsQuery, queryKeys, type SmartViewCounts } from "../index";

import { createPowerSyncSingle, DEFAULT_THROTTLE_MS } from "./create-power-sync-query";

interface UseSmartViewCountsQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  /** Stale time in ms. Pass your app's OFFLINE_CACHE_STALE_TIME_MS. */
  staleTime?: number;
  /** Optional throttle in ms. Defaults to 300ms. */
  throttleTime?: number;
}

/**
 * Smart-view aggregate counts (all, myDay, important, planned, inbox).
 *
 * Used by: app-layout-content.tsx — single authoritative subscription.
 * The sidebar and dashboard read from the TanStack cache, not this hook.
 */
export function useSmartViewCountsQuery(options: UseSmartViewCountsQueryOptions) {
  return createPowerSyncSingle<SmartViewCounts>({
    queryKey: () => queryKeys.smartViews.counts(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getSmartViewCountsQuery(db, uid) : null;
    },
    enabled: (): boolean => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    suspense: false,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}
